// ---------------------------------------------------------------------------
// A Teya-átirányításon átívelő fizetés nyoma a böngészőben: melyik fizetési zárolásból
// születik a foglalás, és meddig tartja a szerver az időpontot.
//
// Ez az EGYETLEN hely, ami ezt írja, olvassa és törli (`teya.ts` ír, a `usePaymentReturn`
// olvas és töröl, a `main.tsx` induláskor takarít). Szándékosan függőség nélküli modul, hogy az
// induláskori takarítás semmit ne húzzon be a nyitóoldal csomagjába.
//
// Két másolat van. A LAPÉ (`sessionStorage`) az elsődleges: több lapon indított fizetésnél is
// mindegyik lap a sajátját követi, és a kész visszaigazolás újratöltése is működik, amíg a lap
// nyitva van. A KÖZÖS (`localStorage`) a tartalék: a banki megerősítés (3DS) telefonon gyakran
// másik lapon nyitja meg a visszatérési címet, ott a lap tárolója még üres. Egy lap mindig csak
// a SAJÁT fizetésének közös másolatát törli, a másik lapét nem.
//
// A vendég adatai (név, e-mail, telefon, cím) NEM kerülnek ide: a foglalást a szerverről
// kérdezzük le. A régebbi kliens még eltárolta őket; azokat a kulcsokat induláskor töröljük.
// ---------------------------------------------------------------------------

/** A kulcs neve a régi klienssel közös; a régi bejegyzésnek nincs ideje, azt elavultnak vesszük. */
const ORDER_KEY = "pendingBookingId";
/** A Teya rövid rendelésazonosítója: a szerver adja a fizetés indításakor, a Teya ezt adhatja vissza */
const TEYA_ORDER_KEY = "pendingPaymentTeyaOrderId";
const HOLD_UNTIL_KEY = "pendingPaymentHoldUntil";
const SAVED_AT_KEY = "pendingPaymentSavedAt";
const PAYMENT_KEYS = [ORDER_KEY, TEYA_ORDER_KEY, HOLD_UNTIL_KEY, SAVED_AT_KEY];

/**
 * A régebbi kliens lapjának nyoma: a Teyára küldéskor írta, időbélyeggel, ezért induláskor még
 * átvesszük (`adoptLegacyTabRecord`), ha friss; így a telepítés pillanatában fizető vendég is
 * visszatalál. A közös másolatának (`pendingBookingId`) nincs ideje, az elavultnak számít. Két
 * órával a telepítés után már nincs mit átvenni: az átvétel a következő módosításkor törölhető.
 */
const LEGACY_TAB_ORDER_KEY = "pendingPaymentOrderId";
const LEGACY_TAB_SAVED_AT_KEY = "pendingPaymentTimestamp";

/** Régebbi kliensek kulcsai. A `pendingBookingData` a vendég személyes adatait tartotta. */
const LEGACY_KEYS = [
  "pendingBookingData",
  LEGACY_TAB_ORDER_KEY,
  "pendingPaymentAmount",
  "pendingPaymentCurrency",
  LEGACY_TAB_SAVED_AT_KEY,
];

/**
 * Ennyi után a nyom elavult, és eldobjuk. A fizetési zárolás 20 perc, lejárta után a szerver még
 * 40 percig vár egy késő visszahívásra (`functions/src/lib/temporaryLocks.js`): egy órán túl
 * ebből a fizetésből már nem születhet foglalás, a két óra ráhagyás. Enélkül egy megosztott
 * gépen a bezárt, félbehagyott fizetés azonosítója örökre ott maradna, és aki legközelebb a
 * visszatérési oldalt megnyitja, az előző vendég foglalását látná.
 */
const MAX_AGE_MS = 2 * 60 * 60 * 1000;

export interface PendingPayment {
  /** A fizetési zárolás azonosítója (`temp_...`) */
  orderId: string;
  /** Ugyanennek a Teya rövid azonosítója; a régi kliens nyomán nincs */
  teyaOrderId: string | null;
  /** Mikor indult a fizetés (ms): ettől számít az elavulás */
  savedAt: number;
  /** Meddig tartja a szerver a fizetési zárolást; `null`, ha nem tudjuk */
  holdUntil: Date | null;
  /**
   * `tab`: ez a lap indította, tehát a zárolás tulajdonos-tokenje (`getLockOwnerToken`, laponként
   * egyedi) is az övé, és a rácson szabadnak látja a saját időpontját. `shared`: egy másik lap.
   */
  source: "tab" | "shared";
}

// A tároló elérése dobhat (letiltott süti, régi Safari privát mód). Ettől a fizetés nem akadhat
// el: legfeljebb a visszatérési oldal nem tudja követni a foglalást, és ezt meg is mondja.
const withStorage = <T>(run: (tab: Storage, shared: Storage) => T, fallback: T): T => {
  try {
    return run(window.sessionStorage, window.localStorage);
  } catch (error) {
    console.warn("A böngésző tárolója nem érhető el:", error);
    return fallback;
  }
};

const removeKeys = (storage: Storage, keys: string[]) => keys.forEach((key) => storage.removeItem(key));

const writeRecord = (
  storage: Storage,
  record: Pick<PendingPayment, "orderId" | "teyaOrderId" | "savedAt" | "holdUntil">,
) => {
  storage.setItem(ORDER_KEY, record.orderId);
  storage.setItem(SAVED_AT_KEY, String(record.savedAt));
  if (record.teyaOrderId) storage.setItem(TEYA_ORDER_KEY, record.teyaOrderId);
  else storage.removeItem(TEYA_ORDER_KEY);
  if (record.holdUntil) storage.setItem(HOLD_UNTIL_KEY, record.holdUntil.toISOString());
  else storage.removeItem(HOLD_UNTIL_KEY);
};

const isFreshAt = (savedAt: number, now: number): boolean =>
  Number.isFinite(savedAt) && savedAt > 0 && now - savedAt <= MAX_AGE_MS;

const isFresh = (storage: Storage, now: number): boolean =>
  isFreshAt(Number(storage.getItem(SAVED_AT_KEY)), now);

/** A régi kliens friss lapnyomát a mai alakra írja (a zárolás lejáratát az nem tárolta). */
const adoptLegacyTabRecord = (tab: Storage, now: number) => {
  const orderId = tab.getItem(LEGACY_TAB_ORDER_KEY);
  const savedAt = Number(tab.getItem(LEGACY_TAB_SAVED_AT_KEY));
  if (!orderId || isFresh(tab, now) || !isFreshAt(savedAt, now)) return;
  writeRecord(tab, { orderId, teyaOrderId: null, savedAt, holdUntil: null });
};

/** Csak ha a tárolóban ennek a fizetésnek a nyoma áll: egy másik lap fizetéséhez nem nyúlunk. */
const removeIfOwn = (storage: Storage, orderId: string) => {
  if (storage.getItem(ORDER_KEY) === orderId) removeKeys(storage, PAYMENT_KEYS);
};

/**
 * Ugyanaz a fizetés-e, amit a visszatérési cím megnevez: a Teya a rövid azonosítót adhatja
 * vissza, a tárolóban mindkettő áll. A rövidet a szerver képzi (`toTeyaOrderId`,
 * `functions/src/lib/teyaOrderId.js`) és a fizetés indításakor visszaadja, a kliens nem
 * számolja újra: így a képzés egy helyen él.
 */
export const isSamePayment = (pending: PendingPayment, urlOrderId: string): boolean =>
  urlOrderId === pending.orderId || urlOrderId === pending.teyaOrderId;


/** Közvetlenül a Teyára küldés előtt: melyik fizetésből tér majd vissza a vendég. */
export const rememberPendingPayment = (
  orderId: string,
  teyaOrderId: string | null,
  holdUntil: Date | null,
) =>
  withStorage((tab, shared) => {
    for (const storage of [tab, shared]) {
      writeRecord(storage, { orderId, teyaOrderId, savedAt: Date.now(), holdUntil });
    }
  }, undefined);

const readFrom = (storage: Storage, source: PendingPayment["source"]): PendingPayment | null => {
  const orderId = storage.getItem(ORDER_KEY);
  if (!orderId || !isFresh(storage, Date.now())) return null;
  const holdUntil = new Date(storage.getItem(HOLD_UNTIL_KEY) ?? "");
  return {
    orderId,
    teyaOrderId: storage.getItem(TEYA_ORDER_KEY),
    savedAt: Number(storage.getItem(SAVED_AT_KEY)),
    holdUntil: Number.isNaN(holdUntil.getTime()) ? null : holdUntil,
    source,
  };
};

/** A lap saját fizetése, vagy ha annak nincs nyoma, a böngészőben legutóbb indított. */
export const readPendingPayment = (): PendingPayment | null =>
  withStorage((tab, shared) => readFrom(tab, "tab") ?? readFrom(shared, "shared"), null);

/**
 * A közös másolatból átvett fizetést a lap a sajátjának is felírja (a 3DS új lapja): így a
 * visszaigazolás újratöltve is megmarad, miután a közös másolat a megerősítéssel elment. A
 * zárolás lejáratát NEM viszi át: a zárolás tulajdonos-tokenje nem ennek a lapnak a tokenje,
 * tehát itt a „fenntartjuk” ígéret hamis lenne.
 */
export const keepInThisTab = (pending: PendingPayment) =>
  withStorage((tab) => writeRecord(tab, { ...pending, holdUntil: null }), undefined);

/**
 * A fizetés sorsa eldőlt (létrejött a foglalás, kézi rendezésre vár, vagy a lekérdezés végleg
 * elakadt): a közös másolat megy,
 * egy megosztott gépen se maradjon nyoma. A lapé marad, hogy a visszaigazolás újratöltve is
 * megjelenjen; a lappal együtt az is elmegy.
 */
export const finishPendingPayment = (orderId: string) =>
  withStorage((_tab, shared) => removeIfOwn(shared, orderId), undefined);

/** A fizetésből nem lesz foglalás (megszakítva, elbukott, hibás azonosító): minden nyoma megy. */
export const clearPendingPayment = (orderId: string) =>
  withStorage((tab, shared) => {
    removeIfOwn(tab, orderId);
    removeIfOwn(shared, orderId);
  }, undefined);

/** Induláskor: a régebbi kliens kulcsai (köztük a vendég személyes adatai) és az elavult nyomok. */
export const cleanUpPaymentStorage = () =>
  withStorage((tab, shared) => {
    const now = Date.now();
    adoptLegacyTabRecord(tab, now);
    for (const storage of [tab, shared]) {
      removeKeys(storage, LEGACY_KEYS);
      if (!isFresh(storage, now)) removeKeys(storage, PAYMENT_KEYS);
    }
  }, undefined);
